import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  adminApiService,
  type TeamBillingDetail, type AdHocChargeResult,
  type AdminPaymentMethod, type PaymentHistory, type BillingChargeRow, type BillingCreditRow,
  type BillingPeriodsResult, type BillingPeriodRow, type AdminInvoice, type RenderConcurrency,
} from '../../services/adminApiService'
import { ApiError } from '../../services/BaseApiService'
import type { AdminTeam } from '../../types'
import { colors, fontSize, radius, spacing, tracking } from '../../styles/theme'
import { Button, Input, Select, SectionCard, ConfirmModal } from '../../components/ui'
import AdminTabs from './AdminTabs'

// Server guard rails (AdminController.Min/MaxAdHocChargeCents).
const MIN_CENTS = 50
const MAX_CENTS = 10_000_000

const CHARGE_STATUS: Record<number, string> = { 10: 'Pending', 20: 'Succeeded', 30: 'Failed', 40: 'Action needed' }
const REFUND_STATUS: Record<number, string> = { 10: 'Pending', 20: 'Succeeded', 30: 'Failed' }
const REFUND_FAILED = 30
const dollars = (c: number) => `$${(c / 100).toFixed(2)}`
const fmtDate = (s: string) => new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
const fmtMethod = (m: AdminPaymentMethod) =>
  m.methodType === 'us_bank_account'
    ? `Bank ····${m.last4 ?? '????'}`
    : `${m.brand ?? 'card'} ····${m.last4 ?? '????'} (exp ${m.expiryMonth ?? '??'}/${m.expiryYear ?? '??'})`

const fmtCard = (c: TeamBillingDetail['card']) =>
  c ? `${c.brand ?? 'card'} ····${c.last4 ?? '????'} (exp ${c.expiryMonth ?? '??'}/${c.expiryYear ?? '??'})` : null

export default function AdminBillingPage() {
  const [teams, setTeams] = useState<AdminTeam[]>([])
  const [teamId, setTeamId] = useState<string>('')
  const [detail, setDetail] = useState<TeamBillingDetail | null>(null)
  const [loadErr, setLoadErr] = useState('')
  // Bumped after an ad-hoc charge so the payment history picks the new ledger row up.
  const [historyVersion, setHistoryVersion] = useState(0)
  // Each team selection takes a token; a response for an earlier selection is dropped, so switching
  // teams quickly never shows one team's billing under another's name.
  const selectToken = useRef(0)

  useEffect(() => {
    adminApiService.getTeams(1, 200)
      .then(r => setTeams(r.items))
      .catch(e => setLoadErr(e instanceof ApiError ? e.message : 'Failed to load teams'))
  }, [])

  async function selectTeam(id: string) {
    const token = ++selectToken.current
    setTeamId(id); setDetail(null); setLoadErr('')
    if (!id) return
    try {
      const d = await adminApiService.getTeamBillingDetail(id)
      if (token === selectToken.current) setDetail(d)
    } catch (e) {
      if (token === selectToken.current) setLoadErr(e instanceof ApiError ? e.message : 'Failed to load team billing')
    }
  }

  const teamName = teams.find(t => t.teamId === teamId)?.name ?? teamId

  return (
    <div>
      <AdminTabs />
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, margin: 0, letterSpacing: tracking.tight, marginBottom: spacing.xl }}>
        Payments
      </h1>

      {loadErr && <div style={{ marginBottom: spacing.lg }}><Banner kind="error">{loadErr}</Banner></div>}

      <div style={{ maxWidth: 680 }}>
        <div style={{ maxWidth: 420, marginBottom: spacing.xl }}>
          <Select
            label="Team"
            placeholder={teams.length ? 'Select a team…' : 'Loading teams…'}
            value={teamId}
            isSearchable
            onChange={v => selectTeam(String(v))}
            options={teams.map(t => ({ value: t.teamId, label: t.name }))}
          />
        </div>

        {teamId && detail && (
          <>
            <TeamSummary detail={detail} />
            {detail.renderConcurrency && <RenderConcurrencyPanel teamId={teamId} initial={detail.renderConcurrency} />}
            <PaymentMethods teamId={teamId} teamName={teamName} providerEnabled={detail.providerEnabled} />
            <AdHocCharge teamId={teamId} teamName={teamName} detail={detail} onCharged={() => setHistoryVersion(v => v + 1)} />
            <CreditPanel teamId={teamId} />
            <InvoicesPanel teamId={teamId} />
            <BillingStatusPanel teamId={teamId} />
            <PaymentHistoryPanel teamId={teamId} refreshKey={historyVersion} />
          </>
        )}
      </div>
    </div>
  )
}

function TeamSummary({ detail }: { detail: TeamBillingDetail }) {
  return (
    <div style={{
      fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.md,
    }}>
      <div>Plan: <strong style={{ color: colors.text }}>{detail.planCode}</strong>{detail.billingExempt && ' · billing-exempt'}</div>
      {detail.allowOverage !== undefined && (
        <div data-testid="admin-overage-settings">Past included pages: <strong style={{ color: colors.text }}>
          {detail.allowOverage === false
            ? 'renders stop (overage off)'
            : detail.overageLimitDollars != null
              ? `overage billed, up to $${detail.overageLimitDollars.toLocaleString()}/month`
              : 'overage billed, no limit'}</strong> · set by the team</div>
      )}
      <div>Default card: {fmtCard(detail.card)
        ? <strong style={{ color: colors.text }}>{fmtCard(detail.card)}</strong>
        : <span style={{ color: colors.error }}>none on file</span>}</div>
      {!detail.providerEnabled && <div style={{ color: colors.error }}>Payment provider is not configured in this environment.</div>}
    </div>
  )
}

// ── Render concurrency (negotiated override) ────────────────────────────────
function RenderConcurrencyPanel({ teamId, initial }: { teamId: string; initial: RenderConcurrency }) {
  const [rc, setRc] = useState(initial)
  const [value, setValue] = useState(initial.override?.toString() ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEffect(() => { setRc(initial); setValue(initial.override?.toString() ?? '') }, [initial])

  const parsed = value.trim() === '' ? null : Number(value)
  const valid = parsed === null || (Number.isInteger(parsed) && parsed >= rc.min && parsed <= rc.max)

  async function save(limit: number | null) {
    setBusy(true); setErr('')
    try {
      const r = await adminApiService.setTeamRenderConcurrency(teamId, limit)
      setRc(r.renderConcurrency); setValue(r.renderConcurrency.override?.toString() ?? '')
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to save the limit') }
    finally { setBusy(false) }
  }

  return (
    <SectionCard title="Render concurrency" subtitle={`Up to ${rc.effective} documents rendering at once, across every server. Plan default ${rc.planDefault}${rc.override != null ? `; negotiated override ${rc.override}` : ''}.`} style={{ marginBottom: spacing.xl }}>
      {err && <div style={{ marginBottom: spacing.md }}><Banner kind="error">{err}</Banner></div>}
      <div style={{ display: 'flex', gap: spacing.md, alignItems: 'flex-end' }}>
        <div style={{ width: 200 }}>
          <Input label={`Override (${rc.min}–${rc.max}, empty = plan default)`} type="number" min={rc.min} max={rc.max} step="1"
            value={value} onChange={e => setValue(e.target.value)} placeholder={String(rc.planDefault)} />
        </div>
        <Button disabled={busy || !valid} onClick={() => save(parsed)}>Save</Button>
        {rc.override != null && <Button variant="ghost" disabled={busy} onClick={() => save(null)}>Use plan default</Button>}
      </div>
      {!valid && <div style={{ marginTop: spacing.sm }}><Banner kind="error">{`Enter a whole number from ${rc.min} to ${rc.max}, or leave it empty.`}</Banner></div>}
    </SectionCard>
  )
}

// ── Payment methods ─────────────────────────────────────────────────────────
function PaymentMethods({ teamId, teamName, providerEnabled }: { teamId: string; teamName: string; providerEnabled: boolean }) {
  const [methods, setMethods] = useState<AdminPaymentMethod[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [removeTarget, setRemoveTarget] = useState<AdminPaymentMethod | null>(null)

  const load = useCallback(async () => {
    try { setMethods(await adminApiService.getPaymentMethods(teamId)) }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to load payment methods') }
  }, [teamId])
  useEffect(() => { load() }, [load])

  async function setDefault(m: AdminPaymentMethod) {
    setBusy(true); setErr('')
    try { await adminApiService.setDefaultPaymentMethod(teamId, m.billingPaymentMethodId); await load() }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to set default') }
    finally { setBusy(false) }
  }

  async function remove() {
    if (!removeTarget) return
    const m = removeTarget; setRemoveTarget(null); setBusy(true); setErr('')
    try { await adminApiService.removePaymentMethod(teamId, m.billingPaymentMethodId); await load() }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to remove method') }
    finally { setBusy(false) }
  }

  return (
    <SectionCard title="Payment methods" style={{ marginBottom: spacing.xl }}>
      {err && <div style={{ marginBottom: spacing.md }}><Banner kind="error">{err}</Banner></div>}
      {methods.length === 0 && <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>No payment methods on file.</div>}
      {methods.map(m => (
        <div key={m.billingPaymentMethodId} style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: '6px 0', borderTop: `1px solid ${colors.border}` }}>
          <span style={{ fontSize: fontSize.sm, color: colors.text }}>{fmtMethod(m)}</span>
          {m.isDefault && <span style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.primary, background: colors.primaryLight, borderRadius: radius.sm, padding: '2px 8px' }}>default</span>}
          {!m.verified && <span style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.error, background: colors.errorLight, borderRadius: radius.sm, padding: '2px 8px' }}>pending verification</span>}
          <div style={{ flex: 1 }} />
          {!m.isDefault && m.verified && <Button size="sm" variant="ghost" disabled={busy || !providerEnabled} onClick={() => setDefault(m)}>Make default</Button>}
          <Button size="sm" variant="danger" disabled={busy} onClick={() => setRemoveTarget(m)}>Remove</Button>
        </div>
      ))}
      <ConfirmModal
        open={!!removeTarget}
        title="Remove payment method"
        message={removeTarget ? `Remove ${fmtMethod(removeTarget)} from ${teamName}? It is detached at the provider and cannot be restored here.` : ''}
        confirmLabel="Remove"
        danger
        onConfirm={remove}
        onCancel={() => setRemoveTarget(null)}
      />
    </SectionCard>
  )
}

// ── Ad-hoc charge (unchanged behavior) ───────────────────────────────────────
function AdHocCharge({ teamId, teamName, detail, onCharged }: { teamId: string; teamName: string; detail: TeamBillingDetail; onCharged: () => void }) {
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [result, setResult] = useState<AdHocChargeResult | null>(null)
  const [error, setError] = useState('')

  const amountCents = useMemo(() => Math.round(Number(amount) * 100), [amount])
  const amountValid = Number.isFinite(amountCents) && amountCents >= MIN_CENTS && amountCents <= MAX_CENTS
  const canCharge = !!detail.providerEnabled && !!detail.hasCustomer && !!detail.card
    && amountValid && description.trim().length > 0 && !busy

  async function doCharge() {
    setConfirm(false); setBusy(true); setResult(null); setError('')
    try {
      const r = await adminApiService.chargeTeamAdHoc(teamId, amountCents, description.trim())
      setResult(r)
      if (r.succeeded) { setAmount(''); setDescription('') }
      // A declined charge is recorded in the ledger too, so refresh history either way.
      onCharged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Charge failed')
    } finally { setBusy(false) }
  }

  return (
    <SectionCard title="Charge a customer" subtitle="Charges the team's default card immediately, outside the monthly usage cycle. Recorded in the ledger with your identity and reason." style={{ marginBottom: spacing.xl }}>
      <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.md }}>
        <div style={{ width: 160 }}>
          <Input label="Amount (USD)" type="number" min="0.50" step="0.01" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="25.00" />
        </div>
      </div>
      <div style={{ marginBottom: spacing.lg }}>
        <Input label="Description / reason" value={description} maxLength={255} onChange={e => setDescription(e.target.value)} placeholder="e.g. Manual invoice — Oct enterprise overage" />
      </div>
      {amount && !amountValid && <div style={{ marginBottom: spacing.md }}><Banner kind="error">Amount must be between $0.50 and $100,000.</Banner></div>}
      <Button variant="danger" disabled={!canCharge} loading={busy} onClick={() => setConfirm(true)}>
        Charge {amountValid ? dollars(amountCents) : ''}
      </Button>
      {error && <div style={{ marginTop: spacing.md }}><Banner kind="error">{error}</Banner></div>}
      {result && (
        <div style={{ marginTop: spacing.md }}>
          <Banner kind={result.succeeded ? 'success' : 'error'}>
            {result.succeeded ? `Charged ${dollars(result.amountCents)} — ${result.providerChargeRef}` : `Charge not completed: ${result.failureMessage ?? result.failureCode ?? 'declined'}`}
          </Banner>
        </div>
      )}
      <ConfirmModal
        open={confirm}
        title="Charge this card?"
        message={`Charge ${teamName} ${dollars(amountCents)} now for "${description.trim()}". This moves real money and cannot be undone here.`}
        confirmLabel={`Charge ${dollars(amountCents)}`}
        danger
        onConfirm={doCharge}
        onCancel={() => setConfirm(false)}
      />
    </SectionCard>
  )
}

// ── Hosted invoices ─────────────────────────────────────────────────────────────
function InvoicesPanel({ teamId }: { teamId: string }) {
  const [invoices, setInvoices] = useState<AdminInvoice[]>([])
  const [lines, setLines] = useState<{ description: string; amount: string }[]>([{ description: '', amount: '' }])
  const [memo, setMemo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [voidId, setVoidId] = useState<AdminInvoice | null>(null)

  const load = useCallback(async () => {
    try { setInvoices(await adminApiService.listInvoices(teamId)) }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to load invoices') }
  }, [teamId])
  useEffect(() => { load() }, [load])

  const parsed = lines
    .map(l => ({ description: l.description.trim(), amountCents: Math.round(Number(l.amount) * 100) }))
    .filter(l => l.description && Number.isFinite(l.amountCents) && l.amountCents > 0)
  const canIssue = parsed.length > 0 && parsed.length === lines.filter(l => l.description.trim() || l.amount).length && !busy

  async function issue() {
    setBusy(true); setErr('')
    try {
      await adminApiService.createInvoice(teamId, parsed, memo.trim() || null)
      setLines([{ description: '', amount: '' }]); setMemo(''); await load()
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to issue invoice') }
    finally { setBusy(false) }
  }

  async function doVoid() {
    if (!voidId) return
    const inv = voidId; setVoidId(null); setBusy(true); setErr('')
    try { await adminApiService.voidInvoice(inv.billingInvoiceId); await load() }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to void') }
    finally { setBusy(false) }
  }

  const updateLine = (i: number, k: 'description' | 'amount', v: string) =>
    setLines(ls => ls.map((l, j) => j === i ? { ...l, [k]: v } : l))

  return (
    <SectionCard title="Hosted invoices" subtitle="Issues a Stripe-hosted invoice the customer pays on Stripe's page. Separate from the monthly metered charge." style={{ marginBottom: spacing.xl }}>
      {err && <div style={{ marginBottom: spacing.md }}><Banner kind="error">{err}</Banner></div>}

      {invoices.length > 0 && (
        <div style={{ marginBottom: spacing.lg }}>
          {invoices.map(inv => (
            <div key={inv.billingInvoiceId} style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: '4px 0', borderTop: `1px solid ${colors.border}`, fontSize: fontSize.sm }}>
              <span style={{ color: colors.textSecondary }}>{fmtDate(inv.createdAt)}</span>
              <span>{dollars(inv.amountCents)}</span>
              <span style={{ color: inv.status === 'paid' ? colors.success : inv.status === 'void' ? colors.textSecondary : colors.text }}>{inv.status}</span>
              {inv.hostedInvoiceUrl && <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>view</a>}
              <div style={{ flex: 1 }} />
              {(inv.status === 'open' || inv.status === 'draft') && <Button size="sm" variant="ghost" disabled={busy} onClick={() => setVoidId(inv)}>Void</Button>}
            </div>
          ))}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.md }}>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.sm }}>
            <div style={{ flex: 1 }}><Input inputSize="sm" placeholder="Description" value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} /></div>
            <div style={{ width: 110 }}><Input inputSize="sm" type="number" step="0.01" inputMode="decimal" placeholder="USD" value={l.amount} onChange={e => updateLine(i, 'amount', e.target.value)} /></div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', marginBottom: spacing.md }}>
          <Button size="sm" variant="ghost" onClick={() => setLines(ls => [...ls, { description: '', amount: '' }])}>+ line</Button>
          <div style={{ flex: 1 }}><Input inputSize="sm" placeholder="Memo (optional)" value={memo} onChange={e => setMemo(e.target.value)} /></div>
          <Button size="sm" loading={busy} disabled={!canIssue} onClick={issue}>Issue invoice</Button>
        </div>
      </div>

      <ConfirmModal
        open={!!voidId}
        title="Void invoice"
        message={voidId ? `Void the ${dollars(voidId.amountCents)} invoice? It can no longer be paid.` : ''}
        confirmLabel="Void"
        danger
        onConfirm={doVoid}
        onCancel={() => setVoidId(null)}
      />
    </SectionCard>
  )
}

// ── Billing status & dunning controls ──────────────────────────────────────────
const BILLING_STATUS: Record<number, { text: string; color: string }> = {
  10: { text: 'OK', color: '' },
  20: { text: 'Past due', color: '' },
  30: { text: 'Suspended', color: '' },
}
const PERIOD_STATUS: Record<number, string> = { 10: 'Computed', 20: 'Charging', 30: 'Paid', 40: 'Failed', 50: 'Carried forward', 60: 'Zero', 70: 'Deferred while on Free' }

function BillingStatusPanel({ teamId }: { teamId: string }) {
  const [data, setData] = useState<BillingPeriodsResult | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState('')
  const [note, setNote] = useState('')

  const load = useCallback(async () => {
    try { setData(await adminApiService.getBillingPeriods(teamId)) }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to load billing status') }
  }, [teamId])
  useEffect(() => { load() }, [load])

  async function retry(p: BillingPeriodRow) {
    setBusy(p.billingPeriodId); setErr(''); setNote('')
    try {
      const r = await adminApiService.retryPeriod(p.billingPeriodId)
      setNote(r.succeeded ? 'Charge succeeded.' : r.processing ? 'Charge is processing (ACH).' : `Not completed: ${r.failureMessage ?? 'declined'}`)
      await load()
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Retry failed') }
    finally { setBusy(null) }
  }

  async function resume() {
    setBusy('resume'); setErr('')
    try { await adminApiService.resumeBilling(teamId); await load() }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Resume failed') }
    finally { setBusy(null) }
  }

  const status = data ? (BILLING_STATUS[data.billingStatus] ?? { text: String(data.billingStatus), color: '' }) : null
  const suspended = data?.billingStatus === 30
  const unpaid = (data?.periods ?? []).filter(p => p.status === 40 || p.status === 20)

  return (
    <SectionCard title="Billing status" style={{ marginBottom: spacing.xl }}>
      {err && <div style={{ marginBottom: spacing.md }}><Banner kind="error">{err}</Banner></div>}
      {note && <div style={{ marginBottom: spacing.md }}><Banner kind="success">{note}</Banner></div>}
      {status && (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md, fontSize: fontSize.base }}>
          Status: <strong style={{ color: data!.billingStatus === 10 ? colors.success : colors.error }}>{status.text}</strong>
          {data!.failedAttemptCount > 0 && <span style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>· {data!.failedAttemptCount} failed attempt(s)</span>}
          <div style={{ flex: 1 }} />
          {suspended && <Button size="sm" variant="secondary" loading={busy === 'resume'} onClick={resume}>Resume billing</Button>}
        </div>
      )}
      {unpaid.length === 0 && <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>No unpaid periods.</div>}
      {unpaid.map(p => (
        <div key={p.billingPeriodId} style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: '4px 0', borderTop: `1px solid ${colors.border}`, fontSize: fontSize.sm }}>
          <span>{fmtDate(p.periodStart)}</span>
          <span style={{ color: colors.error }}>{PERIOD_STATUS[p.status] ?? p.status}</span>
          <span>{dollars(p.totalCents)}</span>
          <div style={{ flex: 1 }} />
          <Button size="sm" variant="ghost" loading={busy === p.billingPeriodId} onClick={() => retry(p)}>Retry now</Button>
        </div>
      ))}
    </SectionCard>
  )
}

// ── Account credit ────────────────────────────────────────────────────────────
function CreditPanel({ teamId }: { teamId: string }) {
  const [balance, setBalance] = useState<number | null>(null)
  const [credits, setCredits] = useState<BillingCreditRow[]>([])
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    try {
      const h = await adminApiService.getPaymentHistory(teamId)
      setBalance(h.creditBalanceCents); setCredits(h.credits)
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to load credit') }
  }, [teamId])
  useEffect(() => { load() }, [load])

  const cents = Math.round(Number(amount) * 100)
  const valid = Number.isFinite(cents) && cents !== 0

  async function grant() {
    setBusy(true); setErr('')
    try {
      await adminApiService.grantCredit(teamId, cents, reason.trim() || null)
      setAmount(''); setReason(''); await load()
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to grant credit') }
    finally { setBusy(false) }
  }

  return (
    <SectionCard
      title="Account credit"
      subtitle="Credit offsets the next metered invoice before the card is charged. A negative amount is a debit adjustment."
      style={{ marginBottom: spacing.xl }}
    >
      <div style={{ fontSize: fontSize.base, marginBottom: spacing.md }}>
        Balance: <strong style={{ color: (balance ?? 0) >= 0 ? colors.success : colors.error }}>
          {balance === null ? '…' : dollars(balance)}
        </strong>
      </div>
      {err && <div style={{ marginBottom: spacing.md }}><Banner kind="error">{err}</Banner></div>}
      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end', marginBottom: spacing.md, flexWrap: 'wrap' }}>
        <div style={{ width: 140 }}>
          <Input label="Amount (USD)" type="number" step="0.01" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="25.00" />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Input label="Reason" value={reason} maxLength={255} onChange={e => setReason(e.target.value)} placeholder="e.g. goodwill for outage" />
        </div>
        <Button size="sm" disabled={!valid || busy} onClick={grant}>Grant credit</Button>
      </div>
      {credits.slice(0, 8).map(c => (
        <div key={c.billingCreditId} style={{ display: 'flex', gap: spacing.sm, padding: '3px 0', borderTop: `1px solid ${colors.border}`, fontSize: fontSize.sm }}>
          <span style={{ color: colors.textSecondary }}>{fmtDate(c.createdAt)}</span>
          <span style={{ color: c.amountCents >= 0 ? colors.success : colors.text }}>{c.amountCents >= 0 ? '+' : ''}{dollars(c.amountCents)}</span>
          <span style={{ color: colors.textSecondary }}>{c.appliedToPeriodId ? 'applied to invoice' : (c.reason ?? '—')}</span>
        </div>
      ))}
    </SectionCard>
  )
}

// ── Payment history + refunds ─────────────────────────────────────────────────
function PaymentHistoryPanel({ teamId, refreshKey }: { teamId: string; refreshKey: number }) {
  const [history, setHistory] = useState<PaymentHistory | null>(null)
  const [err, setErr] = useState('')
  const [refundTarget, setRefundTarget] = useState<BillingChargeRow | null>(null)

  const load = useCallback(async () => {
    try { setHistory(await adminApiService.getPaymentHistory(teamId)); setErr('') }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed to load history') }
  }, [teamId, refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [load])

  // cents already refunded per charge (pending + succeeded; failed refunds don't count).
  const refundedByCharge = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of history?.refunds ?? []) {
      if (r.status === REFUND_FAILED) continue
      m.set(r.billingChargeId, (m.get(r.billingChargeId) ?? 0) + r.amountCents)
    }
    return m
  }, [history])

  const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', fontSize: fontSize.xs, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }
  const td: React.CSSProperties = { padding: '6px 8px', fontSize: fontSize.sm, color: colors.text, borderBottom: `1px solid ${colors.border}` }

  return (
    <SectionCard title="Payment history">
      {err && <div style={{ marginBottom: spacing.md }}><Banner kind="error">{err}</Banner></div>}
      {history && history.charges.length === 0 && <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>No charges yet.</div>}
      {history && history.charges.length > 0 && (
        <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Date</th><th style={th}>Amount</th><th style={th}>Status</th><th style={th}>Description</th><th style={th}></th></tr></thead>
          <tbody>
            {history.charges.map(c => {
              const refunded = refundedByCharge.get(c.billingChargeId) ?? 0
              const remaining = c.amountCents - refunded
              const canRefund = c.status === 20 && remaining > 0
              return (
                <tr key={c.billingChargeId}>
                  <td style={td}>{fmtDate(c.attemptedAt)}</td>
                  <td style={td}>{dollars(c.amountCents)}{refunded > 0 && <span style={{ color: colors.textSecondary }}> (−{dollars(refunded)})</span>}</td>
                  <td style={td}>{CHARGE_STATUS[c.status] ?? c.status}</td>
                  <td style={td}>{c.description ?? (c.billingPeriodId ? 'Monthly usage' : '—')}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{canRefund && <Button size="sm" variant="ghost" onClick={() => setRefundTarget(c)}>Refund</Button>}</td>
                </tr>
              )
            })}
          </tbody>
        </table></div>
      )}
      {refundTarget && (
        <RefundModal
          charge={refundTarget}
          remaining={refundTarget.amountCents - (refundedByCharge.get(refundTarget.billingChargeId) ?? 0)}
          onClose={() => setRefundTarget(null)}
          onDone={() => { setRefundTarget(null); load() }}
        />
      )}
    </SectionCard>
  )
}

function RefundModal({ charge, remaining, onClose, onDone }: { charge: BillingChargeRow; remaining: number; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState((remaining / 100).toFixed(2))
  const [reason, setReason] = useState('requested_by_customer')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const cents = Math.round(Number(amount) * 100)
  const valid = Number.isFinite(cents) && cents > 0 && cents <= remaining

  async function submit() {
    setBusy(true); setError('')
    try {
      const r = await adminApiService.refundCharge(charge.billingChargeId, cents, reason)
      if (r.succeeded || r.pending) onDone()
      else setError(r.failureMessage ?? 'Refund failed')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Refund failed')
    } finally { setBusy(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: colors.surface, borderRadius: radius.md, padding: spacing.lg, width: 360, maxWidth: '100%' }}>
        <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.sm }}>Refund charge</div>
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
          Up to {dollars(remaining)} of {dollars(charge.amountCents)}.
        </div>
        <div style={{ marginBottom: spacing.md }}>
          <Input label="Amount (USD)" type="number" min="0.01" step="0.01" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div style={{ marginBottom: spacing.lg }}>
          <Select label="Reason" value={reason} onChange={v => setReason(String(v))} options={[
            { value: 'requested_by_customer', label: 'Requested by customer' },
            { value: 'duplicate', label: 'Duplicate' },
            { value: 'fraudulent', label: 'Fraudulent' },
          ]} />
        </div>
        {error && <div style={{ marginBottom: spacing.md }}><Banner kind="error">{error}</Banner></div>}
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="danger" loading={busy} disabled={!valid} onClick={submit}>Refund {valid ? dollars(cents) : ''}</Button>
        </div>
      </div>
    </div>
  )
}

function Banner({ kind, children }: { kind: 'error' | 'success'; children: React.ReactNode }) {
  const map = {
    error: { bg: colors.errorLight, fg: colors.error, bd: colors.errorBorder },
    success: { bg: colors.successLight, fg: colors.success, bd: colors.successBorder },
  }[kind]
  return (
    <div style={{ background: map.bg, color: map.fg, border: `1px solid ${map.bd}`, borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm }}>{children}</div>
  )
}
